/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockItem } from "../Stock/types";
import { toBaseQty, unitOptions } from "../Stock/units";
import { formatQty } from "../Stock/formatQty";
import type { CountEntry } from "./types";

type Props = {
  item: StockItem;
  entries: CountEntry[];
  /** Expected level in base units (current level, or as recorded once posted). */
  expected: number;
  readOnly: boolean;
  onChange: (entries: CountEntry[]) => void;
};

/** Counted total in base units, or null if nothing was entered. */
export function countedQty(item: StockItem, entries: CountEntry[]): number | null {
  const filled = entries.filter((e) => e.qty > 0 || e.qty === 0);
  if (filled.length === 0) return null;
  return filled.reduce((sum, e) => sum + (toBaseQty(item, e.qty, e.unit) ?? 0), 0);
}

// One stock item in a count: "4 bottle + 300 ml", with the expected level and
// what posting would adjust.
export const CountLineRow = defineComponent<Props>({
  name: "CountLineRow",
  props: ["item", "entries", "expected", "readOnly", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const update = (index: number, patch: Partial<CountEntry>) =>
      props.onChange(props.entries.map((e, i) => (i === index ? { ...e, ...patch } : e)));

    return () => {
      const item = props.item;
      const counted = countedQty(item, props.entries);
      const difference = counted == null ? null : counted - props.expected;
      const value = difference == null ? null : difference * item.avg_cost;
      const units = unitOptions(item);

      return (
        <tr>
          <td>
            <div class="font-medium">{item.name}</div>
            {item.stock_category && <div class="text-xs text-base-content/50">{item.stock_category}</div>}
          </td>
          <td class="whitespace-nowrap text-sm text-base-content/70">
            {formatQty(props.expected, item.base_unit, item.units)}
          </td>
          <td>
            <div class="flex flex-wrap items-center gap-1">
              {props.entries.map((entry, index) => (
                <span key={index} class="flex items-center gap-1">
                  {index > 0 && <span class="text-base-content/40">+</span>}
                  <input
                    type="number"
                    min="0"
                    step="any"
                    class="input input-bordered input-sm w-20"
                    disabled={props.readOnly}
                    value={Number.isNaN(entry.qty) ? "" : entry.qty}
                    onInput={(e: Event) => {
                      const raw = (e.target as HTMLInputElement).value;
                      update(index, { qty: raw === "" ? NaN : Number(raw) });
                    }}
                  />
                  <select
                    class="select select-bordered select-sm w-24"
                    disabled={props.readOnly}
                    value={entry.unit}
                    onChange={(e: Event) => update(index, { unit: (e.target as HTMLSelectElement).value })}
                  >
                    {units.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </span>
              ))}
              {!props.readOnly && (
                <button
                  class="btn btn-ghost btn-xs"
                  title={t("inventory.counts.add_entry")}
                  onClick={() => props.onChange([...props.entries, { qty: NaN, unit: item.base_unit }])}
                >
                  <span class="material-symbols-outlined text-sm">add</span>
                </button>
              )}
            </div>
          </td>
          <td class="whitespace-nowrap text-right text-sm">
            {difference == null ? (
              <span class="text-base-content/30">—</span>
            ) : (
              <span class={difference < 0 ? "text-error" : difference > 0 ? "text-success" : ""}>
                {`${difference > 0 ? "+" : ""}${Number(difference.toFixed(2))} ${item.base_unit}`}
              </span>
            )}
          </td>
          <td class="whitespace-nowrap text-right text-sm font-medium">
            {value == null ? "—" : value.toFixed(2)}
          </td>
        </tr>
      );
    };
  },
});
